import {Link, Outlet} from 'react-router-dom';
import {
  Button,
  NavItem,
  NavUser,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarNavigation,
  SidebarNavigationProps,
  SidebarProvider,
  SidebarRail,
  TeamSwitcher,
  TeamSwitcherProps,
} from '@tinct/web-components';
import {User} from '@tinct/core-api-models';
import {ArrowLeftIcon} from "lucide-react";
import {ReactNode} from "react";

type LayoutProps = {
  connectedUser: User;
  mainNavigationGroups?: SidebarNavigationProps[];
  workspaces?: TeamSwitcherProps;
  settingsUrl?: string;
  footerNavigationGroups?: SidebarNavigationProps[];
  cardPlan?: ReactNode;
  navUserItems?: NavItem[]
  backLink?: string;
  headerAction?: ReactNode;
  version?: string;
  /** Rendered full-width above the page content (e.g. past-due invoice warning). */
  banner?: ReactNode;
};

export function UserLayout(props: LayoutProps) {
  const {
    connectedUser,
    mainNavigationGroups,
    workspaces,
    footerNavigationGroups = [],
    cardPlan,
    navUserItems = [],
    backLink,
    headerAction,
    version,
    banner,
  } = props;

  return (
    <div>
      <SidebarProvider>
        <Sidebar collapsible="icon" className="sidebar-height">
          <SidebarHeader className='gap-0'>
            {backLink && (
              <div className={'m-2 group-data-[collapsible=icon]:mx-0'}>
                <Link to={backLink}>
                  <Button variant={'outline'} size={'icon'} className="group-data-[collapsible=icon]:size-8 size-auto px-2 py-1">
                    <ArrowLeftIcon />
                    <span className="group-data-[collapsible=icon]:hidden">Back</span>
                  </Button>
                </Link>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div className="flex-1 min-w-0">
                {workspaces && <TeamSwitcher {...workspaces} />}
              </div>
              {headerAction && (
                <div className="shrink-0 group-data-[collapsible=icon]:hidden ">
                  {headerAction}
                </div>
              )}
            </div>
          </SidebarHeader>
          <SidebarContent>
            {mainNavigationGroups &&
              mainNavigationGroups.map((navGroup, index) => (
                <div className='mx-2 group-data-[collapsible=icon]:mx-0'  key={index}>
                  <SidebarNavigation
                    sectionName={navGroup.sectionName}
                    items={navGroup.items}
                  />
                </div>
              ))}
          </SidebarContent>
          <SidebarFooter className={'p-0'}>
            {footerNavigationGroups &&
              footerNavigationGroups.map((navGroup, index) => (
                <div className='mx-2 group-data-[collapsible=icon]:mx-0'  key={index}>
                  <SidebarNavigation
                    sectionName={navGroup.sectionName}
                    items={navGroup.items}
                  />
                </div>
              ))}
            <div className={'p-2 pt-0 gap-2 flex flex-col'}>
              {cardPlan}
              {version && (
                <span className="text-xs text-muted-foreground text-center group-data-[collapsible=icon]:hidden">
                  v{version}
                </span>
              )}
              <NavUser
                user={{
                  name:
                    `${connectedUser?.firstName || ''} ${connectedUser?.lastName || ''}`.trim() ||
                    'Guest User',
                  avatar: connectedUser?.profileImage?.url ?? undefined,
                  email: connectedUser?.email || '',
                }}
                items={navUserItems}
              />

            </div>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset>
          {banner}
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
